import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tKey } from 'ember-intl';
import importAsset from 'lumen/helpers/import-asset';
import type Highlight from 'lumen/models/roi-trophies/highlight';
import type { ReservationMilestoneData } from 'lumen/models/roi-trophies/highlight';
import lottieAnimation from 'lumen/modifiers/lottie-animation';

import GeneralRoiTrophiesContentTrophyModalLayout from './trophy-modal-layout';

type Tier = 'repeat' | '1000' | '500' | '100' | '10';

const MILESTONE_KEYS: Record<Tier, { body: string; heading: string }> = {
  repeat: {
    heading: tKey('roi_trophies.reservation_milestone.heading_repeat'),
    body: tKey('roi_trophies.reservation_milestone.body_repeat'),
  },
  '1000': {
    heading: tKey('roi_trophies.reservation_milestone.heading_1000'),
    body: tKey('roi_trophies.reservation_milestone.body_1000'),
  },
  '500': {
    heading: tKey('roi_trophies.reservation_milestone.heading_500'),
    body: tKey('roi_trophies.reservation_milestone.body_500'),
  },
  '100': {
    heading: tKey('roi_trophies.reservation_milestone.heading_100'),
    body: tKey('roi_trophies.reservation_milestone.body_100'),
  },
  '10': {
    heading: tKey('roi_trophies.reservation_milestone.heading_10'),
    body: tKey('roi_trophies.reservation_milestone.body_10'),
  },
};

interface GeneralRoiTrophiesContentReservationMilestoneSignature {
  Args: {
    highlight: Highlight;
  };
}

export default class GeneralRoiTrophiesContentReservationMilestone extends Component<GeneralRoiTrophiesContentReservationMilestoneSignature> {
  @service declare intl: Services['intl'];

  get body(): string {
    return this.intl.t(MILESTONE_KEYS[this.tier].body);
  }

  get data(): ReservationMilestoneData {
    return this.args.highlight.highlightData as ReservationMilestoneData;
  }

  get heading(): string {
    return this.intl.t(MILESTONE_KEYS[this.tier].heading);
  }

  private get tier(): Tier {
    const { pickup } = this.data;
    if (pickup > 1000) {
      return 'repeat';
    }
    if (pickup === 1000) {
      return '1000';
    }
    if (pickup >= 500) {
      return '500';
    }
    if (pickup >= 100) {
      return '100';
    }
    return '10';
  }

  <template>
    <GeneralRoiTrophiesContentTrophyModalLayout>
      <:content>
        <div class="flex flex-col items-center gap-300">
          <div
            class="heading-1"
            data-test-id="reservation-milestone-heading"
          >{{this.heading}}</div>
          <p
            class="text-300--regular text-neutral-subdued"
            data-test-id="reservation-milestone-body"
          >{{this.body}}</p>
        </div>
      </:content>
      <:animation>
        <div
          {{lottieAnimation
            (importAsset "animations/roi-trophies/total-reservations.json")
            false
          }}
        />
      </:animation>
    </GeneralRoiTrophiesContentTrophyModalLayout>
  </template>
}
